import { Icon, Typography } from '@mui/material';
import { JustificationStatus } from '@schooly/api';
import { getJustificationStatusIcon } from '@schooly/components/justifications';
import { InformationBlock, PlusIcon, SmallButton, WarningBlock } from '@schooly/style';
import { FC, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

type AttendanceSidebarNotTakenWarningProps = {
  trackingPointLabel: string;
  group?: { id: string; name: string };
  link?: string;
};

export const AttendanceSidebarNotTakenWarning: FC<AttendanceSidebarNotTakenWarningProps> = ({
  trackingPointLabel,
  group,
  link,
}) => {
  const { formatMessage } = useIntl();

  const showButton = !!group && !!link;

  return (
    <WarningBlock
      sx={{
        p: 1,
        flexDirection: 'row',
        gap: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography>
        {group
          ? formatMessage(
              { id: 'attendance-AttendanceNotTakenForGroup' },
              { groupName: `"${group.name}"` },
            )
          : formatMessage({ id: 'attendance-NoAttendanceYet' })}
      </Typography>
      {showButton && (
        <Link to={link} target="_blank">
          <SmallButton startIcon={<PlusIcon />} sx={{ textWrap: 'nowrap' }}>
            {formatMessage({ id: 'attendance-TakeSession' }, { trackingPoint: trackingPointLabel })}
          </SmallButton>
        </Link>
      )}
    </WarningBlock>
  );
};

type AttendanceSidebarFooterBlockProps = {
  icon: ReactNode;
  textId: string;
  color: string;
  bgcolor: string;
};

export const AttendanceSidebarFooterBlock: FC<AttendanceSidebarFooterBlockProps> = ({
  icon,
  textId,
  color,
  bgcolor,
}) => {
  const { formatMessage } = useIntl();

  return (
    <InformationBlock
      direction="row"
      alignItems="center"
      gap={1}
      sx={(theme) => ({
        p: 1,
        bgcolor,
        border: theme.mixins.borderValue(),
        borderColor: color,
        color,
        justifyContent: 'flex-start',
      })}
    >
      <Icon>{icon}</Icon>
      <Typography>{formatMessage({ id: textId })}</Typography>
    </InformationBlock>
  );
};

type AttendanceSidebarJustificationWarningProps = {
  status: JustificationStatus.Pending | JustificationStatus.Closed;
};

export const AttendanceSidebarJustificationWarning: FC<
  AttendanceSidebarJustificationWarningProps
> = ({ status }) => {
  const willBeClosed = status === JustificationStatus.Closed;

  return (
    <AttendanceSidebarFooterBlock
      icon={getJustificationStatusIcon(status)}
      textId={
        willBeClosed
          ? 'attendance-justification-OnceSavedClosed'
          : 'attendance-justification-OnceSaved'
      }
      color={willBeClosed ? 'common.grey3' : 'common.orange'}
      bgcolor={willBeClosed ? 'common.lightBg' : 'common.orange5'}
    />
  );
};
